import { AnomalyRecord } from '../types/bitcoin';
import { toast } from 'sonner';

export class NotificationService {
  private hasPermission: boolean = false;

  constructor() {
    this.requestPermission();
  }

  // Request browser notification permission
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.hasPermission = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.hasPermission = permission === 'granted';
      return this.hasPermission;
    }

    return false;
  }

  // Send browser notification for anomaly
  sendAnomalyNotification(anomaly: AnomalyRecord): void {
    const { blockHeight, verification } = anomaly;
    const anomalyCount = verification.anomalies.length;
    const severityLevel = verification.anomalies[0]?.severity || 'unknown';

    // Toast notification (always shown)
    toast.error(`Anomaly detected in Block #${blockHeight}`, {
      description: `${anomalyCount} timestamp anomalies found (${severityLevel} severity)`,
      duration: 5000,
      action: {
        label: 'View Details',
        onClick: () => this.focusOnBlock(blockHeight),
      },
    });

    // Browser notification (if permission granted)
    if (this.hasPermission) {
      const notification = new Notification(`Bitcoin Timestamp Anomaly`, {
        body: `Block #${blockHeight}: ${anomalyCount} anomalies detected (${severityLevel})`,
        icon: '/bitcoin-icon.png', // You can add this icon later
        tag: `anomaly-${blockHeight}`, // Prevent duplicate notifications
        requireInteraction: severityLevel === 'critical',
      });

      notification.onclick = () => {
        window.focus();
        this.focusOnBlock(blockHeight);
        notification.close();
      };

      // Auto-close after 8 seconds unless critical
      if (severityLevel !== 'critical') {
        setTimeout(() => notification.close(), 8000);
      }
    }

    console.log(`🚨 Anomaly notification sent for block ${blockHeight}`);
  }

  // Send success notification for consensus
  sendConsensusNotification(blockHeight: number, sourceCount: number): void {
    toast.success(`Block #${blockHeight} verified`, {
      description: `Timestamp consensus achieved across ${sourceCount} sources`,
      duration: 3000,
    });
  }

  // Send new block notification
  sendNewBlockNotification(blockHeight: number): void {
    toast.info(`New block mined`, {
      description: `Block #${blockHeight} added to the blockchain`,
      duration: 2000,
    });
  }

  // Send API error notification
  sendApiErrorNotification(apiName: string, error: string): void {
    toast.warning(`${apiName} API Error`, {
      description: error,
      duration: 4000,
    });
  }

  // Send connection status notification
  sendConnectionNotification(connected: boolean): void {
    if (connected) {
      toast.success('Connected to Bitcoin network', {
        description: 'Real-time monitoring active',
        duration: 3000,
      });
    } else {
      toast.error('Connection lost', {
        description: 'Attempting to reconnect...',
        duration: 5000,
      });
    }
  }

  // Send prediction update notification
  sendPredictionNotification(nextBlockTime: number, confidence: number): void {
    const minutes = Math.floor(nextBlockTime / 60);
    const seconds = Math.floor(nextBlockTime % 60);
    
    toast.info('Block prediction updated', {
      description: `Next block in ~${minutes}m ${seconds}s (${Math.round(confidence * 100)}% confidence)`,
      duration: 3000,
    });
  }

  // Helper method to focus on specific block
  private focusOnBlock(blockHeight: number): void {
    // This could scroll to the block in the UI or switch to verification tab
    console.log(`Focusing on block ${blockHeight}`);
    
    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('focusBlock', { 
      detail: { blockHeight } 
    }));
  }

  // Get notification permission status
  getPermissionStatus(): string {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  // Check if notifications are enabled
  isEnabled(): boolean {
    return this.hasPermission;
  }

  // Test notification (for user settings)
  sendTestNotification(): void {
    if (this.hasPermission) {
      const notification = new Notification('Bitcoin Monitor Test', {
        body: 'Notifications are working correctly!',
        icon: '/bitcoin-icon.png',
        tag: 'test-notification',
      });

      setTimeout(() => notification.close(), 3000);
    }

    toast.success('Test notification sent', {
      description: 'Check your browser notifications',
      duration: 2000,
    });
  }
}

// Export singleton instance
export const notifications = new NotificationService();